import React from 'react'
import { EditUserProp } from './types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { useToasts } from "react-toast-notifications";
import { Edit } from '@mui/icons-material';
import { AttributeContainer, CrossContainer, HeaderContainer, InputsContainer } from './styles';
import { CircularProgress, Divider, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { Close } from '@mui/icons-material';
import theme from '../../theme/theme';
import { getUserById } from '../../services/user/getUser';
import { RequestModal } from '../../components/RequestModal';
import { putUserType, putBlockedStatus, putDeletedStatus } from '../../services/user/putUser';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    height: 'auto',
    minWidth: '380px',
    maxHeight: '700px',
    overflow: 'auto',
    borderRadius: '20px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};


const EditUser: React.FC<EditUserProp> = ({
    userID,
    onClose,
}) => {
    const { addToast } = useToasts();
    // ------------------- JWT -------------------
    const jwt = localStorage.getItem('jwt');

    // ------------------- STATES -------------------
    const [open, setOpen] = React.useState(false);
    const [loadingUser, setLoadingUser] = React.useState(false);
    const [loadingEditUser, setLoadingEditUser] = React.useState(false);
    const [warningMessage, setWarningMessage] = React.useState("");
    const [isModified, setIsModified] = React.useState(false);
    const [invalid, setInvalid] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setWarningMessage('');
        setIsModified(false);
        setInvalid(false);
        setUserInfo(null);
        setUserName('');
        setUserLastName('');
        setUserEmail('');
        setUserEmailVerified(false);
        setUserBlocked(false);
        setUserDeleted(false);
        setUserType(0);
        setMBlocked(false);
        setMDeleted(false);
        setMType(false);
        onClose();
    };

    // ------------------- USER INFO -------------------
    const [userInfo, setUserInfo] = React.useState<any>(null);
    const [userName, setUserName] = React.useState('');
    const [userLastName, setUserLastName] = React.useState('');
    const [userEmail, setUserEmail] = React.useState('');
    const [userEmailVerified, setUserEmailVerified] = React.useState(false);
    const [userBlocked, setUserBlocked] = React.useState(false);
    const [userDeleted, setUserDeleted] = React.useState(false);
    const [userType, setUserType] = React.useState(0);

    const [mBlocked, setMBlocked] = React.useState(false);
    const [mDeleted, setMDeleted] = React.useState(false);
    const [mType, setMType] = React.useState(false);
    const [error, setError] = React.useState(false);

    // ------------------- API REQUEST -------------------
    const getUser = async () => {
        handleOpen();
        setLoadingUser(true);
        await getUserById(userID, jwt!)
            .then((res: any) => {
                if (res && res.data) {
                    setUserInfo(res.data.user);
                }
            })
            .catch((err: any) => {
                console.log(err, "err");
            });

        setLoadingUser(false);
    }

    // ------------------- EDIT FUNCTION -------------------
    const editUser = async () => {
        setLoadingEditUser(true);
        try {
            if (mBlocked) {
                const response = await putBlockedStatus(jwt!, userEmail, userBlocked);
                if (response.status < 300) {
                    addToast("Estatus de bloqueado modificado", {
                        appearance: "success",
                        autoDismiss: true,
                    });
                } else {
                    setError(true);
                    addToast("Hubo un error al modificar el estatus de bloqueado", {
                        appearance: "error",
                        autoDismiss: true,
                    });
                }
            }
            if (mDeleted) {
                const response = await putDeletedStatus(jwt!, userEmail);
                if (response.status < 300) {
                    addToast("Estatus de eliminado modificado", {
                        appearance: "success",
                        autoDismiss: true,
                    });
                } else {
                    setError(true);
                    addToast("Hubo un error al modificar el estatus de eliminado", {
                        appearance: "error",
                        autoDismiss: true,
                    });
                }
            }
            if (mType) {
                const response = await putUserType(jwt!, userID, userName, userLastName, userType);
                if (response.status < 300) {
                    // setWarningMessage("El usuario ha sido modificado.");
                    // setIsModified(true);
                    addToast("Tipo de usuario modificado", {
                        appearance: "success",
                        autoDismiss: true,
                    });
                } else {
                    // setWarningMessage("Deben existir al menos dos administradores.");
                    // setInvalid(true);
                    setError(true);
                    addToast("Hubo un error al modificar el tipo de usuario", {
                        appearance: "error",
                        autoDismiss: true,
                    });
                }
            }
            if (error) {
                setWarningMessage("Ocurrió un error. Inténtelo de nuevo.");
                setInvalid(true);
            } else {
                setWarningMessage("El usuario ha sido modificado.");
                setIsModified(true);
            }
        } catch (err) {
            setWarningMessage("Ocurrió un error. Inténtelo de nuevo.");
            setInvalid(true);
        } finally {
            setLoadingEditUser(false);
        }

        setLoadingEditUser(false);
    }

    // ------------------- USE EFFECT -------------------
    React.useEffect(() => {
        if (userInfo) {
            setUserName(userInfo.name);
            setUserLastName(userInfo.lastname);
            setUserEmail(userInfo.email);
            setUserEmailVerified(userInfo.email_verified);
            setUserBlocked(userInfo.blocked);
            setUserDeleted(userInfo.deleted);
            setUserType(userInfo.user_type);
        }
    }, [userInfo]);

    // ------------------- HANDLERS -------------------
    const handleBlockedChange = (event: SelectChangeEvent) => {
        setMBlocked(!mBlocked);
        if (event.target.value === 'true') {
            setUserBlocked(true);
        } else {
            setUserBlocked(false);
        }
    };

    const handleDeletedChange = (event: SelectChangeEvent) => {
        setMDeleted(!mDeleted);
        if (event.target.value === 'true') {
            setUserDeleted(true);
        } else {
            setUserDeleted(false);
        }
    }

    const handleUserTypeChange = (event: SelectChangeEvent) => {
        setMType(!mType);
        if (event.target.value === 'admin') {
            setUserType(1);
        } else {
            setUserType(2);
        }
    }

    return (
        <>
            {
                isModified ? (
                    <RequestModal
                        title='¡Usuario modificado!'
                        description={warningMessage}
                        onClose={handleClose}
                    />
                ) : (
                    invalid &&
                    <RequestModal
                        title='Ocurrió un error'
                        description={warningMessage}
                        onClose={handleClose}
                    />
                )
            }

            <Button onClick={getUser} style={{ padding: '0px' }} >
                <Edit sx={{ color: theme.color.navy, fontSize: '2rem' }} />
            </Button>
            <Modal
                open={open}
                // onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    {
                        loadingUser &&
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: theme.color.navy }} />
                        </div>
                    }
                    {loadingEditUser ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: theme.color.navy }} />
                        </div>
                    ) : (
                        <>
                            <CrossContainer
                                onClick={handleClose}
                            >
                                <Close />
                            </CrossContainer>
                            <HeaderContainer>Editar usuario</HeaderContainer>
                            <InputsContainer>
                                <AttributeContainer>
                                    <TextField
                                        label="Nombre(s)"
                                        type="text"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        disabled={true}
                                        value={userName}
                                        sx={{ margin: "15px 0", width: '100%' }}
                                        InputProps={{
                                            style: {
                                                borderRadius: "14px",
                                            },
                                        }}
                                    />
                                </AttributeContainer>
                                <AttributeContainer>
                                    <TextField
                                        label="Apellido(s)"
                                        type="text"
                                        variant="outlined"
                                        value={userLastName}
                                        fullWidth
                                        disabled={true}
                                        sx={{ margin: "15px 0", width: '100%' }}
                                        InputProps={{
                                            style: {
                                                borderRadius: "14px",
                                            },
                                        }}
                                    />
                                </AttributeContainer>
                            </InputsContainer>
                            <InputsContainer>
                                <AttributeContainer>
                                    <TextField
                                        label="Correo"
                                        type="text"
                                        variant="outlined"
                                        fullWidth
                                        disabled={true}
                                        value={userEmail}
                                        required
                                        sx={{ margin: "15px 0", width: '100%' }}
                                        InputProps={{
                                            style: {
                                                borderRadius: "14px",
                                            },
                                        }}
                                    />
                                </AttributeContainer>
                            </InputsContainer>
                            <InputsContainer>
                                <AttributeContainer>
                                    <FormControl sx={{ margin: "15px 0", width: '100%' }}>
                                        <InputLabel id="demo-simple-select-autowidth-label">Bloqueado</InputLabel>
                                        <Select
                                            label="Bloqueado"
                                            fullWidth
                                            required
                                            value={userBlocked ? "true" : "false"}
                                            sx={{ borderRadius: '15px' }}
                                            onChange={handleBlockedChange}
                                        >
                                            <MenuItem value="true">Si</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </Select>
                                    </FormControl>
                                </AttributeContainer>
                                <AttributeContainer>
                                    <FormControl sx={{ margin: "15px 0", width: '100%' }}>
                                        <InputLabel>Eliminado</InputLabel>
                                        <Select
                                            label="Eliminado"
                                            fullWidth
                                            required
                                            value={userDeleted ? "true" : "false"}
                                            disabled={!userDeleted ? true : false}
                                            onChange={handleDeletedChange}
                                            sx={{ borderRadius: '15px' }}
                                        >
                                            <MenuItem value="true">Si</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </Select>
                                    </FormControl>
                                </AttributeContainer>
                            </InputsContainer>
                            <AttributeContainer>
                                <FormControl sx={{ margin: "15px 0", width: '100%' }}>
                                    <InputLabel id="demo-simple-select-autowidth-label">Tipo de usuario</InputLabel>
                                    <Select
                                        label="Tipo de usuario"
                                        fullWidth
                                        required
                                        value={userType === 1 ? "admin" : "normal"}
                                        onChange={handleUserTypeChange}
                                        sx={{ borderRadius: '15px' }}
                                    >
                                        <MenuItem value="admin">Administrador</MenuItem>
                                        <MenuItem value="normal">Usuario normal</MenuItem>
                                    </Select>
                                </FormControl>
                            </AttributeContainer>
                            <Divider />
                            <Button
                                variant="contained"
                                onClick={editUser}
                                sx={{
                                    width: "100%",
                                    marginTop: "20px",
                                    borderRadius: "15px",
                                    fontSize: "16px",
                                    backgroundColor: theme.color.navy,
                                    color: theme.color.white,
                                    textTransform: "none",
                                    "&:hover": {
                                        backgroundColor: theme.color.space,
                                    },
                                }}
                            >Guardar
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </>
    )
}

export default EditUser;